/**
 * Unified Doctor Onboarding Service
 * Consolidates all doctor onboarding API calls and business logic
 */
import axiosInstance from '../../api/axiosInstance';
import { validateAndNormalizePhone } from '../../utils/phoneValidation';

const API_BASE = '/api/v1/doctor';

class UnifiedDoctorOnboardingService {
    constructor() {
        this.cache = new Map();
        this.retryCount = 0;
        this.maxRetries = 3;
    }

    /**
     * Get onboarding state and progress
     */
    async getOnboardingState() {
        try {
            const response = await axiosInstance.get(`${API_BASE}/onboarding/state/`);
            const state = response.data;

            // Cache the state
            this.cache.set('onboardingState', state);

            return {
                ...state,
                progress: this.calculateProgress(state),
                nextSteps: this.getNextSteps(state),
                estimatedTime: this.getEstimatedTime(state)
            };
        } catch (error) {
            console.error('Error fetching onboarding state:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Get doctor profile data
     */
    async getProfile() {
        try {
            const response = await axiosInstance.get(`${API_BASE}/profile/`);
            return response.data;
        } catch (error) {
            console.error('Error fetching profile:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Save doctor profile data
     */
    async saveProfile(profileData) {
        try {
            // Validate and normalize phone number
            if (profileData.phone) {
                const phoneValidation = validateAndNormalizePhone(profileData.phone);
                if (!phoneValidation.isValid) {
                    throw new Error(phoneValidation.error);
                }
                profileData.phone = phoneValidation.normalizedNumber;
            }

            // Validate profile data
            const validation = this.validateProfile(profileData);
            if (!validation.isValid) {
                throw new Error(validation.errors.join(', '));
            }

            const response = await axiosInstance.post(`${API_BASE}/profile/`, profileData);

            // Update cache
            this.cache.set('profile', response.data);

            return response.data;
        } catch (error) {
            console.error('Error saving profile:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Get step data for a specific onboarding step
     */
    async getStepData(stepId) {
        try {
            const response = await axiosInstance.get(`${API_BASE}/onboarding/step/${stepId}/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching step data for ${stepId}:`, error);
            throw this.handleError(error);
        }
    }

    /**
     * Save step data for a specific onboarding step
     */
    async saveStepData(stepId, data) {
        try {
            // Validate step data
            const validation = this.validateStepData(stepId, data);
            if (!validation.isValid) {
                throw new Error(validation.errors.join(', '));
            }

            const response = await axiosInstance.post(`${API_BASE}/onboarding/step/${stepId}/`, data);

            // Update cache
            this.cache.set(`step_${stepId}`, response.data);

            return response.data;
        } catch (error) {
            console.error(`Error saving step data for ${stepId}:`, error);
            throw this.handleError(error);
        }
    }

    /**
     * Mark a section as complete
     */
    async completeSection(sectionId) {
        try {
            const response = await axiosInstance.post(`${API_BASE}/onboarding/complete-section/`, {
                section: sectionId
            });

            // Update cache
            const state = this.cache.get('onboardingState');
            if (state) {
                state.completed_sections = [...(state.completed_sections || []), sectionId];
                this.cache.set('onboardingState', state);
            }

            return response.data;
        } catch (error) {
            console.error(`Error completing section ${sectionId}:`, error);
            throw this.handleError(error);
        }
    }

    /**
     * Skip a section
     */
    async skipSection(sectionId) {
        try {
            const response = await axiosInstance.post(`${API_BASE}/onboarding/skip-section/`, {
                section: sectionId
            });

            // Update cache
            const state = this.cache.get('onboardingState');
            if (state) {
                state.skipped_sections = [...(state.skipped_sections || []), sectionId];
                this.cache.set('onboardingState', state);
            }

            return response.data;
        } catch (error) {
            console.error(`Error skipping section ${sectionId}:`, error);
            throw this.handleError(error);
        }
    }

    /**
     * Complete onboarding
     */
    async completeOnboarding() {
        try {
            const response = await axiosInstance.post(`${API_BASE}/onboarding/complete/`);

            // Clear cache
            this.cache.clear();

            return response.data;
        } catch (error) {
            console.error('Error completing onboarding:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Save draft
     */
    async saveDraft(stepId, data) {
        try {
            const response = await axiosInstance.post(`${API_BASE}/onboarding/draft/`, {
                step: stepId,
                data: data
            });

            return response.data;
        } catch (error) {
            console.error(`Error saving draft for ${stepId}:`, error);
            throw this.handleError(error);
        }
    }

    /**
     * Get verification status
     */
    async getVerificationStatus() {
        try {
            const response = await axiosInstance.get(`${API_BASE}/onboarding/verification-status/`);
            return response.data;
        } catch (error) {
            console.error('Error fetching verification status:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Upload document
     */
    async uploadDocument(documentType, file) {
        try {
            const formData = new FormData();
            formData.append('document_type', documentType);
            formData.append('file', file);

            const response = await axiosInstance.post(`${API_BASE}/onboarding/upload-document/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.data;
        } catch (error) {
            console.error(`Error uploading document ${documentType}:`, error);
            throw this.handleError(error);
        }
    }

    /**
     * Get available specializations
     */
    async getSpecializations() {
        try {
            const response = await axiosInstance.get(`${API_BASE}/onboarding/specializations/`);
            return response.data;
        } catch (error) {
            console.error('Error fetching specializations:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Get available time slots
     */
    async getAvailableTimeSlots() {
        try {
            const response = await axiosInstance.get(`${API_BASE}/onboarding/available-time-slots/`);
            return response.data;
        } catch (error) {
            console.error('Error fetching available time slots:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Validate profile data
     */
    validateProfile(profileData) {
        const errors = [];

        // Required fields
        if (!profileData.full_name || profileData.full_name.trim() === '') {
            errors.push('Full name is required');
        }

        if (!profileData.email || profileData.email.trim() === '') {
            errors.push('Email is required');
        }

        if (!profileData.phone || profileData.phone.trim() === '') {
            errors.push('Phone number is required');
        }

        if (!profileData.specialization || profileData.specialization.trim() === '') {
            errors.push('Specialization is required');
        }

        if (!profileData.license_number || profileData.license_number.trim() === '') {
            errors.push('License number is required');
        }

        // Optional field validations
        if (profileData.email && !this.isValidEmail(profileData.email)) {
            errors.push('Please enter a valid email address');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Validate step data
     */
    validateStepData(stepId, data) {
        const errors = [];

        switch (stepId) {
            case 'basic_info':
                return this.validateProfile(data);

            case 'professional_details':
                if (!data.qualifications || data.qualifications.trim() === '') {
                    errors.push('Qualifications are required');
                }
                if (!data.experience || data.experience < 0) {
                    errors.push('Experience is required and must be a positive number');
                }
                break;

            case 'documents':
                if (!data.license_document) {
                    errors.push('License document is required');
                }
                if (!data.id_document) {
                    errors.push('ID document is required');
                }
                break;

            case 'clinic_setup':
                if (!data.clinic_name || data.clinic_name.trim() === '') {
                    errors.push('Clinic name is required');
                }
                if (!data.clinic_address || data.clinic_address.trim() === '') {
                    errors.push('Clinic address is required');
                }
                break;

            case 'availability':
                if (!data.working_hours || Object.keys(data.working_hours).length === 0) {
                    errors.push('Working hours are required');
                }
                if (!data.timezone || data.timezone.trim() === '') {
                    errors.push('Timezone is required');
                }
                break;

            case 'billing':
                if (!data.payment_methods || data.payment_methods.length === 0) {
                    errors.push('At least one payment method is required');
                }
                if (!data.subscription_plan || data.subscription_plan.trim() === '') {
                    errors.push('Subscription plan is required');
                }
                break;

            default:
                // Generic validation
                break;
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Calculate onboarding progress
     */
    calculateProgress(state) {
        const totalSteps = 6; // basic_info, professional_details, documents, clinic_setup, availability, billing
        const completedSteps = state.completed_sections?.length || 0;
        return Math.round((completedSteps / totalSteps) * 100);
    }

    /**
     * Get next steps
     */
    getNextSteps(state) {
        const completedSections = state.completed_sections || [];
        const nextSteps = [];

        const steps = ['basic_info', 'professional_details', 'documents', 'clinic_setup', 'availability', 'billing'];

        for (const step of steps) {
            if (!completedSections.includes(step)) {
                nextSteps.push(step);
            }
        }

        return nextSteps;
    }

    /**
     * Get estimated time remaining
     */
    getEstimatedTime(state) {
        const completedSections = state.completed_sections || [];
        const timeEstimates = {
            basic_info: 3,
            professional_details: 5,
            documents: 10,
            clinic_setup: 8,
            availability: 5,
            billing: 7
        };

        let totalTime = 0;
        for (const [step, time] of Object.entries(timeEstimates)) {
            if (!completedSections.includes(step)) {
                totalTime += time;
            }
        }

        return totalTime;
    }

    /**
     * Helper methods
     */
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Handle API errors
     */
    handleError(error) {
        if (error.response) {
            // Server responded with error status
            const { status, data } = error.response;

            switch (status) {
                case 400:
                    return new Error(data.detail || 'Invalid request data');
                case 401:
                    return new Error('Authentication required');
                case 403:
                    return new Error('Access denied');
                case 404:
                    return new Error('Resource not found');
                case 500:
                    return new Error('Server error. Please try again later');
                default:
                    return new Error(data.detail || 'An error occurred');
            }
        } else if (error.request) {
            // Network error
            return new Error('Network error. Please check your connection');
        } else {
            // Other error
            return new Error(error.message || 'An unexpected error occurred');
        }
    }

    /**
     * Clear cache
     */
    clearCache() {
        this.cache.clear();
    }

    /**
     * Get cached data
     */
    getCachedData(key) {
        return this.cache.get(key);
    }

    /**
     * Set cached data
     */
    setCachedData(key, data) {
        this.cache.set(key, data);
    }
}

// Create singleton instance
const unifiedDoctorOnboardingService = new UnifiedDoctorOnboardingService();

export default unifiedDoctorOnboardingService;